import toast, {Toaster} from 'react-hot-toast'
import {Button} from 'react-bootstrap'
import {useFileUpload} from '../../hooks/useFileUpload'
import {handleApiError} from '../../../helpers/errorHandler'
import {uploadNFC} from './api'
import {ErrorMessage, Form, Formik, FormikHelpers} from 'formik'
import * as Yup from 'yup'
import FormikControl, {ErrorFeedback} from '../../../components/formikControl/FormikControl'
import FileDrop from '../../../components/excelUpload/FileDrop'
import UploadFile from '../../../components/excelUpload/UploadFile'

interface FormValues {
  type: string
  file: File | null
}

const NFCUpload = ({
  initialValues,
  onAfterSubmit,
}: {
  initialValues?: {type: string}
  onAfterSubmit: () => void
}) => {
  const {uploadFile, isUploading, uploadProgress} = useFileUpload()

  const submitHandler = async (values: FormValues, {setSubmitting}: FormikHelpers<FormValues>) => {
    setSubmitting(true)
    let toastId = ''
    try {
      if (!values.file) throw new Error('No file selected')

      const fileResponse = await uploadFile(values.file)

      toastId = toast.loading('Processing data')

      const payload = {type: values.type, file: fileResponse.data}

      await uploadNFC(payload)
      toast.success('Success', {id: toastId})
      onAfterSubmit()
    } catch (error: any) {
      handleApiError(error, toastId)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {/* <div className="fs-1 mb-3 lead">NFC Bulk Upload</div> */}
      <div className="fv-row card p-6">
        <Formik<FormValues>
          initialValues={{type: initialValues?.type || '', file: null}}
          validationSchema={Yup.object({
            type: Yup.string().required('Type is required'),
            file: Yup.mixed().required('File is required'),
          })}
          onSubmit={submitHandler}
        >
          {({isSubmitting, setFieldValue, setFieldTouched, values, resetForm}) => (
            <Form>
              <FormikControl
                control="select"
                name="type"
                className="mb-5"
                required
                label="Type"
                disabled={!!initialValues?.type}
              >
                <option value="">Select Type</option>
                <option value="student">Student</option>
                <option value="driver">Driver</option>
                <option value="security">Security</option>
              </FormikControl>

              {values.file ? (
                <UploadFile
                  file={values.file}
                  removeFile={() => setFieldValue('file', null)}
                  isUploading={isUploading}
                  uploadProgress={uploadProgress}
                />
              ) : (
                <>
                  <FileDrop
                    onDropRejected={() => toast.error('Only .xlsx files are supported.')}
                    onDropAccepted={(files) => {
                      setFieldValue('file', files[0])
                      setFieldTouched('file', true)
                    }}
                    templateLink="/excel-template/trip-upload-template-model.xlsx"
                    templateFileName="trip-upload-template-model.xlsx"
                  />
                  <ErrorMessage name="file" component={ErrorFeedback} />
                </>
              )}

              <div className="text-center pt-8">
                <button
                  type="reset"
                  onClick={() => resetForm()}
                  className="btn btn-light px-8 me-4 border border-gray-200"
                  disabled={isSubmitting}
                >
                  Reset
                </button>
                <Button
                  variant="light-success"
                  className="pe-3 bg-light-success"
                  disabled={isSubmitting}
                  type="submit"
                >
                  <span className="">
                    Upload <i className="la la-upload fs-4"></i>
                  </span>
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <Toaster />
    </>
  )
}

export default NFCUpload
